import "@findemdev/maui/dist/styles/maui.css";
import React from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AppProviders from "./AppProviders";
import { Sidebar } from "./components/Sidebar";
import { Layout } from "@findemdev/maui";
import { SidebarWrapper, StyledLayoutContent } from "./App.styled";
import CustomRoutes from "./Routes";
import Login from "./components/Common/Login/Login";
import styled from "styled-components";
import { store } from "./store";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import UserContext from "./contexts/UserContext";
import { buildLaunchDarklyContext } from "./utils/launchdarkly";
import {
  getHideLayoutBarsOverride,
  withHideLayoutBarsTrue,
} from "./utils/layoutBars";
import useCheckAppVersion from "./customHooks/useCheckAppVersion";
import ErrorBoundary from "./components/ErrorBoundary";
import { getChunkRetryCleanupUrl } from "./components/ErrorBoundary/chunkLoadRecovery";

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userModel = React.useContext(UserContext);
  const ldClient = useLDClient();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true);
  const layoutBarsOverride = React.useMemo(
    () => getHideLayoutBarsOverride({ search: location.search }),
    [location.search]
  );
  const [stickyHideLayoutBars, setStickyHideLayoutBars] = React.useState(
    () => layoutBarsOverride === true
  );
  const isLogin = location.pathname.startsWith("/login");
  const hideLayoutBars = layoutBarsOverride === true || (layoutBarsOverride === null && stickyHideLayoutBars);

  useCheckAppVersion({ enabled: !isLogin });

  React.useEffect(() => {
    const cleanupUrl = getChunkRetryCleanupUrl({
      currentUrl: window.location.href,
    });

    if (!cleanupUrl) {
      return;
    }

    const nextUrl = new URL(cleanupUrl);

    navigate(
      {
        pathname: nextUrl.pathname,
        search: nextUrl.search,
        hash: nextUrl.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  // Update LaunchDarkly context when user is authenticated
  React.useEffect(() => {
    const user = userModel?.getUser();
    if (ldClient && user?.uid && user?.name && user?.email && user?.account_id) {
      const multiContext = buildLaunchDarklyContext({
        uid: user.uid,
        name: user.name,
        email: user.email,
        accountId: user.account_id,
      });

      ldClient.identify(multiContext).catch((err) => {
        console.error('Failed to identify multi-context in LaunchDarkly:', err);
      });
    }
  }, [ldClient, userModel]);

  React.useEffect(() => {
    if (layoutBarsOverride === true) {
      setStickyHideLayoutBars(true);
      return;
    }

    if (layoutBarsOverride === false) {
      setStickyHideLayoutBars(false);
    }
  }, [layoutBarsOverride]);

  React.useEffect(() => {
    // Keep hideLayoutBars sticky across internal navigation when query params drop.
    if (isLogin || !hideLayoutBars || layoutBarsOverride !== null) {
      return;
    }

    // Re-add hideLayoutBars=true while preserving existing query params.
    const normalizedSearch = withHideLayoutBarsTrue({ search: location.search });

    if (normalizedSearch !== location.search) {
      // Use replace so normalization does not add extra history entries.
      navigate(
        {
          pathname: location.pathname,
          search: normalizedSearch,
          hash: location.hash,
        },
        { replace: true }
      );
    }
  }, [
    hideLayoutBars,
    isLogin,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    layoutBarsOverride,
  ]);

  return (
    <TopLayout>
      <SubLayout>
        {isLogin && (
          <Routes>
            <Route path={"*"} element={<Login />} />
          </Routes>
        )}
        {!isLogin && (
          <>
            {!hideLayoutBars && (
              <SidebarWrapper>
                <Sidebar
                  isSidebarCollapsed={isSidebarCollapsed}
                  setIsSidebarCollapsed={setIsSidebarCollapsed}
                />
              </SidebarWrapper>
            )}
            <StyledLayoutContent
              $sidebarCollapsed={isSidebarCollapsed}
              $sidebarDisabled={hideLayoutBars}
            >
              <CustomRoutes
                hideLayoutBars={hideLayoutBars}
                isSidebarCollapsed={isSidebarCollapsed}
              />
            </StyledLayoutContent>
          </>
        )}
      </SubLayout>
    </TopLayout>
  );
};
const LiftedApp = () => {
  return (
    <Provider store={store}>
      <AppProviders>
        <ErrorBoundary>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ErrorBoundary>
      </AppProviders>
    </Provider>
  );
};

export default Sentry.withProfiler(LiftedApp);

const TopLayout = styled(Layout)`
  max-height: 100vh;
  height: 100vh;
`;

const SubLayout = styled(Layout)`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
`;
